import React from 'react';
import { styled } from '@mui/material/styles';
import { ButtonBase, Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { lighten } from '@mui/system';
import { PricingSheet } from '../../types/PricingSheet';
import { PricingAttributesLabel } from './PricingAttributesLabel';
import { ClientDataFixedColumns } from '../../constants/ClientDataFixedColumns';
import hiddenPriceSrc from '../../images/hiddenPriceIcon.svg';
import { CellMetadata, ClientDataTableRowDiff } from '../../types/ClientData';
import { ContentList } from '../library/ContentList';

export const AttributesButton = styled(ButtonBase)<{
  selected?: boolean;
  changes?: boolean;
}>(({ selected, changes, theme }) => ({
  borderRadius: '8px',
  border: '1px solid #DEDEDE',
  justifyContent: 'space-between',
  padding: '8px 16px 8px 10px',
  color: lighten('#000000', 1 - 0.87),
  ...(selected
    ? {
        width: '264px',
        margin: '0px',
        marginRight: '-1px',
        borderRight: 'none',
        borderTopRightRadius: '0px',
        borderBottomRightRadius: '0px',
        background: changes ? `linear-gradient(to right, ${theme.palette.secondary.main} 7px, #FFFFFF 7px)` : '#FFFFFF',
        '&:hover': {
          cursor: 'default',
        },
      }
    : {
        width: '247px',
        margin: '8px 0px 0px',
        background: changes
          ? `linear-gradient(to right, ${theme.palette.secondary.main} 7px, ${lighten('#000000', 0.92)} 7px)`
          : lighten('#000000', 0.92),
        '&:hover': {
          background: changes
            ? `linear-gradient(to right, ${theme.palette.secondary.main} 7px, ${lighten('#000000', 0.94)} 7px)`
            : lighten('#000000', 0.94),
        },
      }),
}));

const NoMaxWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    disableInteractive
    disableHoverListener={props.disableHoverListener}
    TransitionProps={{ timeout: 600 }}
    title={props.title}
    classes={{ popper: className }}
  >
    {props.children}
  </Tooltip>
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: '600px',
  },
});

export type PricingAttributeProps = {
  selected?: boolean;
  pricingSheet: PricingSheet;
  cellMetadataDiff: CellMetadata[];
  pricingDiffs: {
    table: string;
    changes: ClientDataTableRowDiff[];
  }[];
  onClick?: (id: string) => void;
};

export const PricingAttribute = React.forwardRef<HTMLButtonElement, PricingAttributeProps>(
  ({ selected = false, pricingSheet, cellMetadataDiff, pricingDiffs, onClick }, ref) => {
    const labelRef = React.useRef<HTMLUListElement | null>(null);
    const showTooltip = labelRef?.current ? labelRef.current.offsetWidth < labelRef.current.scrollWidth : false;

    const { id, attributes, prices, changes } = pricingSheet;
    const isHiddenStyle = prices?.every(({ hidden = {} }) =>
      Object.values(hidden).reduce((acc, isHidden) => acc && isHidden, true),
    );

    const hasChanges = (() => {
      if (changes !== undefined) return changes;
      const diffRowIds = Array.from(
        new Set(
          [...pricingDiffs.flatMap(({ changes: c }) => c), ...cellMetadataDiff].map(
            (val) => val[ClientDataFixedColumns.RowId],
          ),
        ),
      );
      return prices?.some(({ [ClientDataFixedColumns.RowId]: rowId }) => diffRowIds.includes(rowId)) || undefined;
    })();

    return (
      <NoMaxWidthTooltip
        key={id}
        disableHoverListener={!showTooltip}
        title={
          <ContentList
            items={attributes
              .filter(({ hide, label, value }) => label && value && !hide)
              .map(({ label, value }) => ({
                label: `${label || value}`,
              }))}
            listStyleType="disc"
            spacing="16px"
          />
        }
      >
        <AttributesButton ref={ref} changes={hasChanges} selected={selected} onClick={() => onClick?.(id)}>
          <PricingAttributesLabel ref={labelRef} pricingSheet={pricingSheet} />
          {isHiddenStyle && <img alt="Hidden price" src={hiddenPriceSrc} />}
        </AttributesButton>
      </NoMaxWidthTooltip>
    );
  },
);
